import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { BiometricProvider } from '@/features/auth/services/biometric-credentials';
import { mmkvStorage } from '@/stores/middleware/mmkv-storage';

/**
 * Non-sensitive companion to the Keychain entries. The real secret (password) lives in the
 * biometric-gated Keychain; this store only keeps what the UI needs to render *without*
 * triggering a biometric prompt: which accounts have biometric login on, and each one's
 * provider/email (for the biometric login screen).
 *
 * Keyed by Firebase `uid` so every account on the device remembers its own biometric setting
 * independently — enabling/disabling on one account never touches another's.
 */
export type BiometricAccount = {
  email: string;
  provider: BiometricProvider;
};

type BiometricState = {
  /** Biometric-login bindings keyed by Firebase uid. Presence of a uid = enabled for it. */
  accounts: Record<string, BiometricAccount>;
};

type BiometricActions = {
  /** Turn biometric login on for `uid` (remembering its provider/email). */
  enable: (account: { uid: string; email: string; provider: BiometricProvider }) => void;
  /** Turn biometric login off for `uid` (leaving other accounts untouched). */
  disable: (uid: string) => void;
};

const initialState: BiometricState = {
  accounts: {},
};

export const useBiometricStore = create<BiometricState & BiometricActions>()(
  persist(
    set => ({
      ...initialState,
      enable: ({ uid, email, provider }) =>
        set(state => ({ accounts: { ...state.accounts, [uid]: { email, provider } } })),
      disable: uid =>
        set(state => {
          if (!(uid in state.accounts)) return state;
          const next = { ...state.accounts };
          delete next[uid];
          return { accounts: next };
        }),
    }),
    {
      name: 'biometric-store',
      storage: createJSONStorage(() => mmkvStorage),
      // v1 introduced the per-account map. The old single-slot shape ({enabled,uid,email,provider})
      // is dropped — its Keychain entry lived under a different (non-uid) service anyway, so the
      // handful of pre-upgrade users simply re-enable biometric once. Fresh, no orphaned "on" flag.
      version: 1,
      migrate: () => initialState,
    },
  ),
);
